import { useCallback, useEffect, useMemo } from 'react';
import deleteIcon from '~/assets/icons/tattoo_delete.svg';
import { ShopListWithScroll } from '~/components/ui/ShopListWithScroll';
import { rage, useInputAction, useObservable } from '~/rage';
import { Gender, InputAction, WalletType } from '~/shared';
import { useBuy } from '../../hooks';
import {
	setActiveList,
	setActiveListIndex,
	setActiveStage,
	setMaxListIndex,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CategoryImages, CategoryNames } from '../CategoryTab';
import { ListTabItem } from '../ListTabItem';

const ListTab = () => {
	const dispatch = useLayoutDispatch();
	const activeListIndex = useLayoutSelector(
		(state) => state.tattooShop.activeListIndex,
	);
	const maxListIndex = useLayoutSelector(
		(state) => state.tattooShop.maxListIndex,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.tattooShop.activeCategory,
	);
	const tattooItems = useObservable(rage.tattooShop.getItems());
	const ownedTattoos = useObservable(rage.tattooShop.getOwnedTattoos());
	const tattooDeletionPrice = useObservable(
		rage.tattooShop.getTattooDeletionPrice(),
	);
	const playerGender = useObservable(rage.player.getPlayerGender());

	const filteredItems = useMemo(() => {
		if (activeCategory === 'delete') {
			return ownedTattoos ?? [];
		}

		return (
			tattooItems?.filter(
				(item) =>
					item.categoryId === activeCategory &&
					(item.gender === playerGender || item.gender === Gender.COMMON),
			) ?? []
		);
	}, [tattooItems, activeCategory, ownedTattoos, playerGender]);

	const { handleBuy } = useBuy();

	const handleCheckInstall = useCallback(
		(index: number) => {
			const item = filteredItems?.[index];
			return ownedTattoos?.some((tattoo) => tattoo.id === item?.id);
		},
		[ownedTattoos, filteredItems],
	);

	const handleSelectItem = useCallback(
		(index: number | null) => {
			if (index !== null) {
				if (index < 0 || index >= filteredItems.length) {
					return;
				}
				if (activeCategory !== 'delete' && handleCheckInstall(index)) {
					return;
				}
			}

			dispatch(setActiveListIndex(index));
		},
		[handleCheckInstall, activeCategory, filteredItems.length],
	);

	const handleBack = useCallback(() => {
		dispatch(setActiveStage(StageTypes.Category));
		rage.tattooShop
			.previewTattooCategory(null)
			.catch((err) => rage.notifications.showException(err));
	}, []);

	const getItemId = useCallback(
		(index: number) => {
			const item = filteredItems?.[index];
			return item ? `tattoo-${item.id}` : '';
		},
		[filteredItems],
	);

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (activeListIndex === null) {
			return;
		}

		handleBuy(WalletType.Cash);
	});

	useEffect(() => {
		if (activeListIndex === null) {
			if (!activeCategory) {
				return;
			}

			rage.tattooShop
				.previewTattooCategory(activeCategory)
				.catch((err) => rage.notifications.showException(err));
			return;
		}

		const item = filteredItems?.[activeListIndex];
		if (!item?.id) {
			return;
		}

		rage.tattooShop
			.previewTattoo(item.id)
			.catch((err) => rage.notifications.showException(err));
	}, [filteredItems, activeListIndex, activeCategory]);

	useEffect(() => {
		dispatch(setMaxListIndex((filteredItems.length || 0) - 1));
		dispatch(setActiveList(filteredItems));
	}, [filteredItems]);

	useEffect(() => {
		if (activeCategory === 'delete' && ownedTattoos?.length === 0) {
			dispatch(setActiveListIndex(null));
			dispatch(setActiveStage(StageTypes.Category));
		}
	}, [activeCategory, ownedTattoos]);

	if (!activeCategory) {
		return;
	}

	return (
		<ShopListWithScroll
			icon={
				activeCategory === 'delete'
					? deleteIcon
					: CategoryImages[activeCategory]
			}
			title={
				activeCategory === 'delete'
					? 'Удаление тату'
					: CategoryNames[activeCategory]
			}
			style={{ marginTop: 30 }}
			handleBack={handleBack}
			activeIndex={activeListIndex}
			maxIndex={maxListIndex}
			handleCheckInstall={
				activeCategory === 'delete' ? () => false : handleCheckInstall
			}
			handleSelect={handleSelectItem}
			height={567}
			enableAutoScroll={true}
			getItemId={getItemId}
		>
			{filteredItems.map((item, index) => (
				<ListTabItem
					item={item}
					index={index}
					activeCategory={activeCategory}
					activeListIndex={activeListIndex}
					handleSelect={handleSelectItem}
					tattooDeletionPrice={tattooDeletionPrice}
					ownedTattoos={ownedTattoos}
					key={index}
				/>
			))}
		</ShopListWithScroll>
	);
};

export { ListTab };
